import { IconButton, Tooltip } from "@mui/material"
import { useRef } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam, useSearchParamsBatch } from "lib/@hooks/use-search-param"
import { setReference } from "lib/@hooks/use-reference"
import Iconify from "minimals-template/components/Iconify"
import { isEnabled } from "lib/@components/feature"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import {
    SHARE_LINK_COMMENT_TARGET_REFERENCE,
    SHARE_LINK_COMMENTS_TAB,
    buildIntroCommentTarget,
    shouldShowShareLinkCommentsTab,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import { FEATURE_FLAG_SHARE_LINK_COMMENTS } from "./share-link-comments-feature-flag"

// Comment entry point for a schedule's introduction. Task comments are created by selecting a task
// part in the schedule body. Clicking stashes the comment target in the right-bar reference and opens
// the Comments tab; the composer there reads the target.
export function ShareLinkCommentTargetButton() {
    const selectedText = useRef("")
    const setSearchParams = useSearchParamsBatch()
    const context = useBoundContext()
    const { isShare, isPDF, schedule, schedulePath } = context
    const [shareLinkId] = useSearchParam("share", null)
    const shareLink = getSharingLink.useResults(shareLinkId)
    // Gate on the LINK's allowComments AND the share-link-comments feature demand. Recipients inherit
    // that demand from the link owner's client auth bundle (ConfigureShareLinkDemands); when the
    // feature/auth bundle is disabled the demand is absent and this entry point closes rather than
    // bleeding through the link's persisted allowComments (FIQ-1668). The server re-checks every write.
    const canShow =
        shouldShowShareLinkCommentsTab({ isShare: !!isShare || !!shareLinkId, shareLinkId, shareLink }) &&
        isEnabled(FEATURE_FLAG_SHARE_LINK_COMMENTS)

    if (!canShow || isPDF) return null

    return (
        <Tooltip title="Comment on introduction">
            <IconButton
                size="small"
                data-cy="share-link-comment-intro-target"
                onMouseDown={captureSelection}
                onClick={selectTarget}
            >
                <Iconify icon="mdi:comment-plus-outline" width={18} height={18} />
            </IconButton>
        </Tooltip>
    )

    function captureSelection(event) {
        event.preventDefault()
        event.stopPropagation()
        selectedText.current = window.getSelection?.()?.toString?.()?.trim?.() ?? ""
    }

    function selectTarget(event) {
        event.stopPropagation()
        const commentTarget = getCommentTarget()
        if (!commentTarget) return
        setReference(
            {
                ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB,
                [SHARE_LINK_COMMENT_TARGET_REFERENCE]: commentTarget,
            },
            "tab-ScheduleRightBar-"
        )
        setSearchParams({ ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB })
    }

    function getCommentTarget() {
        const commentTarget = buildIntroCommentTarget({ schedule, selectedText: selectedText.current })
        if (!commentTarget) return null
        return { ...commentTarget, schedulePath }
    }
}
